import { useState, useEffect } from 'react';
import api from '../services/api';
import { Search, Plus, ClipboardCheck, X, Save, ArrowRight, AlertTriangle, CheckCircle2, History, ListFilter } from 'lucide-react';

export default function InventoryAudit() {
  const [audits, setAudits] = useState([]);
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  
  const [notes, setNotes] = useState('');
  const [auditItems, setAuditItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = async () => {
    try {
      const [auditRes, prodRes] = await Promise.all([
        api.get('/inventory/audits'),
        api.get('/products')
      ]);
      setAudits(auditRes.data);
      setProducts(prodRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { loadData(); }, []);

  const startAudit = () => {
    setAuditItems(products.map(p => ({
      productId: p.id,
      name: p.name,
      sku: p.sku,
      expectedStock: p.stock,
      countedStock: p.stock
    })));
    setIsModalOpen(true);
  };

  const handleSaveAudit = async () => {
    try {
      await api.post('/inventory/audits', {
        notes,
        items: auditItems.map(i => ({ productId: i.productId, countedStock: i.countedStock }))
      });
      setIsModalOpen(false);
      setNotes('');
      loadData();
    } catch (err) {
      alert('Error saving audit');
    }
  };

  const viewAuditDetails = async (id) => {
    const res = await api.get(`/inventory/audits/${id}`);
    setSelectedAudit(res.data);
    setIsDetailsOpen(true);
  };

  const filteredItems = auditItems.filter(i => 
    i.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    i.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 animate-fade-in-up pb-10 max-w-7xl mx-auto">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight flex items-center gap-3">
            <ClipboardCheck size={32} className="text-brand" /> Inventory Audit
          </h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-xs">Verify physical stock and reconcile discrepancies.</p>
        </div>
        <button onClick={startAudit} className="bg-brand hover:bg-brand-light text-white px-6 py-3 rounded-2xl shadow-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all hover:-translate-y-0.5">
          <Plus size={18} /> New Stock Take
        </button>
      </div>

      <div className="bg-theme-card rounded-3xl shadow-soft border border-theme overflow-hidden">
        <div className="p-6 border-b border-theme bg-theme-main/10 flex justify-between items-center">
          <h3 className="text-sm font-black text-theme-muted uppercase tracking-[0.2em] flex items-center gap-2">
            <History size={16} /> Audit History
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                <th className="px-6 py-4">Audit ID / Date</th>
                <th className="px-6 py-4">Performed By</th>
                <th className="px-6 py-4 text-center">Items Checked</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {audits.map(audit => (
                <tr key={audit.id} className="hover:bg-theme-main/10 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="font-black text-theme-main uppercase tracking-tight">#{audit.id}</div>
                    <div className="text-[10px] text-theme-muted font-bold mt-1">{new Date(audit.createdAt).toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-theme-main">{audit.user?.name}</td>
                  <td className="px-6 py-4 text-center font-black text-theme-main">{audit._count?.items}</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                      <CheckCircle2 size={12} /> {audit.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button 
                      onClick={() => viewAuditDetails(audit.id)}
                      className="text-theme-muted hover:text-brand font-black text-[10px] uppercase tracking-widest flex items-center gap-1 ml-auto group-hover:translate-x-1 transition-transform"
                    >
                      Details <ArrowRight size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {audits.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-theme-muted font-bold italic uppercase tracking-widest text-[10px] opacity-50">
                    No audit records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md flex items-start justify-center pt-24 z-50 animate-fade-in p-4">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-4xl border border-theme flex flex-col mb-10">
            <div className="px-8 py-6 border-b border-theme bg-theme-main/10 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Digital Stock Take</h3>
                <p className="text-[10px] text-theme-muted font-black uppercase mt-1 tracking-widest">Verify physical counts against system records.</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-xl transition-colors">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 border-b border-theme bg-theme-main/5 flex gap-4 shrink-0">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" size={18} />
                <input 
                  type="text" 
                  placeholder="Scan SKU or Search Product Name..." 
                  className="w-full bg-theme-main border border-theme rounded-2xl py-3 pl-12 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="w-1/3">
                <input 
                  type="text" 
                  placeholder="Audit Notes (Optional)" 
                  className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-4 text-theme-main font-bold outline-none"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 gap-3">
                {filteredItems.map((item, idx) => {
                  const discrepancy = item.countedStock - item.expectedStock;
                  return (
                    <div key={idx} className={`p-4 rounded-2xl border transition-all flex items-center gap-6 ${discrepancy === 0 ? 'bg-theme-main/10 border-theme' : 'bg-amber-500/5 border-amber-500/20'}`}>
                      <div className="flex-1">
                        <p className="font-black text-theme-main text-sm uppercase tracking-tight">{item.name}</p>
                        <p className="text-[9px] text-theme-muted font-black uppercase tracking-[0.2em] mt-1">{item.sku}</p>
                      </div>
                      <div className="text-center w-24">
                        <p className="text-[9px] font-black text-theme-muted uppercase mb-1">Expected</p>
                        <p className="font-black text-theme-main">{item.expectedStock}</p>
                      </div>
                      <div className="w-32">
                        <p className="text-[9px] font-black text-theme-muted uppercase mb-1 text-center">Physical Count</p>
                        <input 
                          type="number" 
                          value={item.countedStock} 
                          onChange={e => {
                            const newItems = [...auditItems];
                            const itemIdx = auditItems.findIndex(i => i.productId === item.productId);
                            newItems[itemIdx].countedStock = parseInt(e.target.value) || 0;
                            setAuditItems(newItems);
                          }}
                          className="w-full bg-theme-main border border-theme rounded-xl py-2 px-3 text-center font-black text-brand focus:ring-2 focus:ring-brand"
                        />
                      </div>
                      <div className="w-24 text-right">
                        <p className="text-[9px] font-black text-theme-muted uppercase mb-1">Discrepancy</p>
                        <p className={`font-black ${discrepancy > 0 ? 'text-emerald-500' : discrepancy < 0 ? 'text-rose-500' : 'text-theme-muted opacity-30'}`}>
                          {discrepancy > 0 ? `+${discrepancy}` : discrepancy}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-8 border-t border-theme bg-theme-main/10 shrink-0">
              <button 
                onClick={handleSaveAudit}
                className="w-full py-4 bg-brand hover:bg-brand-light text-white rounded-2xl font-black uppercase text-sm tracking-[0.2em] shadow-xl shadow-brand/20 transition-all active:scale-95 flex items-center justify-center gap-3"
              >
                <Save size={20} /> Reconcile & Submit Audit
              </button>
            </div>
          </div>
        </div>
      )}

      {isDetailsOpen && selectedAudit && (
        <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md flex items-start justify-center pt-24 z-50 animate-fade-in p-4">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-2xl border border-theme flex flex-col mb-10">
            <div className="px-8 py-6 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Audit Report Details</h3>
                <p className="text-[10px] text-theme-muted font-black uppercase mt-1 tracking-widest">Reconciliation Log for #{selectedAudit.id}</p>
              </div>
              <button onClick={() => setIsDetailsOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-xl transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-theme-main p-4 rounded-2xl border border-theme">
                  <p className="text-[9px] font-black text-theme-muted uppercase tracking-widest mb-1">Date Performed</p>
                  <p className="font-bold text-theme-main">{new Date(selectedAudit.createdAt).toLocaleString()}</p>
                </div>
                <div className="bg-theme-main p-4 rounded-2xl border border-theme">
                  <p className="text-[9px] font-black text-theme-muted uppercase tracking-widest mb-1">Auditor</p>
                  <p className="font-bold text-theme-main">{selectedAudit.user?.name}</p>
                </div>
              </div>

              {selectedAudit.notes && (
                <div className="bg-amber-500/5 p-4 rounded-2xl border border-amber-500/20 flex items-start gap-3">
                  <AlertTriangle className="text-amber-500 shrink-0" size={18} />
                  <div>
                    <p className="text-[9px] font-black text-amber-500 uppercase tracking-widest mb-1">Auditor Notes</p>
                    <p className="text-xs font-medium text-theme-main">{selectedAudit.notes}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-[9px] font-black text-theme-muted uppercase tracking-widest mb-3">Reconciliation Line Items</p>
                {selectedAudit.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-theme-main/30 rounded-xl border border-theme">
                    <div>
                      <p className="text-xs font-black text-theme-main uppercase tracking-tight">{item.product?.name}</p>
                      <p className="text-[9px] text-theme-muted font-bold mt-1">Exp: {item.expectedStock} | Counted: {item.countedStock}</p>
                    </div>
                    <div className={`text-xs font-black px-3 py-1 rounded-lg ${item.discrepancy > 0 ? 'bg-emerald-500/10 text-emerald-500' : item.discrepancy < 0 ? 'bg-rose-500/10 text-rose-500' : 'bg-theme-main text-theme-muted opacity-50'}`}>
                      {item.discrepancy > 0 ? `+${item.discrepancy}` : item.discrepancy}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
